import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  FormChecklistTemplate,
  useFormChecklistTemplateDestroyMutation,
} from 'graphql-common';
import toast from '@lib/components/Toast/Toast';
import { showGraphQLErrors } from '@lib/utils/graphQLErrors';
import { Modals } from '@lib/enums/modals';
import useSearchParam from '@lib/hooks/useSearchParam';
import usePaginationParams from '@lib/hooks/usePaginationParams';
import { UseModalManagementResult } from '@lib/hooks/useModalManagement';
import { UseBulkSelectResult } from '@lib/hooks/useTableItemsBulkSelect';
import { APP_URLS } from 'constants/urls';
import useFormChecklistTemplatesQueryHook from 'utils/fetch/hooks/useFormChecklistTemplatesQueryHook';

interface Props {
  useBulkSelectResult: UseBulkSelectResult<FormChecklistTemplate>;
  useModalManagementResult: UseModalManagementResult;
}

export interface UseActionsResult {
  listQuery: ReturnType<typeof useFormChecklistTemplatesQueryHook>;
  onAdd: () => void;
  onCloseDestroyModal: () => void;
  onConfirmedDelete: () => void;
  onDestroy: (item?: FormChecklistTemplate) => void;
  onEdit: (item?: FormChecklistTemplate) => void;
  onOpen: (item?: FormChecklistTemplate) => void;
}

export default function useFormChecklistsActions(props: Props) {
  const { useModalManagementResult, useBulkSelectResult } = props;
  const navigate = useNavigate();
  const { openModal, closeModal } = useModalManagementResult;

  const { t } = useTranslation();
  const [searchQuery] = useSearchParam();
  const [paginationParams] = usePaginationParams();

  // Queries
  const listQuery = useFormChecklistTemplatesQueryHook({
    paginationParams,
    searchQuery,
  });

  // Mutations
  const [FormChecklistTemplateDestroyMutation] =
    useFormChecklistTemplateDestroyMutation({
      onError: (error) => showGraphQLErrors(error, t),
    });

  // Actions
  const onAdd = () => {
    navigate(APP_URLS.dashboard.formChecklists.add.path);
  };

  const onEdit = (item?: FormChecklistTemplate) => {
    if (item) {
      navigate(
        APP_URLS.dashboard.formChecklists.edit.getDynamicPath({
          pathParts: {
            id: item.id,
          },
        }),
      );
    }
  };

  const onOpen = (item?: FormChecklistTemplate) => {
    if (item) {
      navigate(
        APP_URLS.dashboard.formChecklists.edit.getDynamicPath({
          pathParts: {
            id: item.id,
          },
        }),
      );
    }
  };

  const onDestroy = (item?: FormChecklistTemplate) => {
    if (item?.id) {
      useBulkSelectResult.handleSelectSingleTableItem(item);
      openModal(Modals.DestroyModal);
    }
  };

  const onCloseDestroyModal = () => {
    closeModal(Modals.DestroyModal);
    useBulkSelectResult.handleUnselectAllTableItems();
  };

  const onConfirmedDelete = () => {
    closeModal(Modals.DestroyModal);
    const id = useBulkSelectResult.selectedSingleTableItem?.id;
    if (id) {
      toast({
        content: t('destroy-checklist-success'),
        closeCallback: () => {
          FormChecklistTemplateDestroyMutation({
            variables: { id },
            onCompleted: () => {
              listQuery.refetch();
              useBulkSelectResult.handleUnselectAllTableItems();
            },
          });
        },
      });
    }
  };

  return {
    listQuery,
    onAdd,
    onCloseDestroyModal,
    onConfirmedDelete,
    onDestroy,
    onEdit,
    onOpen,
  };
}
